import { createContext, useContext, useEffect, useState, ReactNode } from 'react';
import { api } from '../services/apiClient';

export interface User {
  account_id: number;
  email: string;
  firstname: string;
  lastname: string;
  role: 'admin' | 'teacher' | 'student';
  /** Rol crudo del núcleo (owner|manager|staff) — 'admin' agrupa owner+manager y hay
      pantallas (p.ej. permisos de usuarios) que necesitan distinguir al owner. */
  baseRole?: string;
}

interface AuthState {
  user: User | null;
  loading: boolean;
  /** Claves de módulos ACTIVOS. undefined = CARGANDO · null = sin gate (bypass/fallo/alumno) · [] = cargado. */
  modules: string[] | null | undefined;
  /** ¿El módulo está activo? null/undefined → true (no oculta); ver `modules` para distinguir carga. */
  hasModule: (key: string) => boolean;
  /** Sesión "Ver como": el owner que está impersonando (null = sesión normal). */
  impersonator: { id: number; name: string | null } | null;
  /** Plan vencido → modo consulta (403 PLAN_READONLY en altas). Viene de /me/permissions. */
  planReadOnly: boolean;
  /** Plan por vencer (umbral del cron: 5 días mensual / 7 anual). null = nada que avisar. */
  planDueSoon: PlanDueSoon | null;
  login: (username: string, password: string) => Promise<void>;
  logout: () => Promise<void>;
}

export interface PlanDueSoon { days_left: number; expires_at: string; is_trial: boolean }

// El tenant de Let's Move en lealify-base (slug). El núcleo resuelve el tenant por host
// (fachada letsmove.lealify.com tras el cutover) o por tenant_slug (vale ya vía app.lealify.com).
const TENANT_SLUG = 'letsmove';

// Usuario del núcleo (lealify-base): { id, tenant_id, role: owner|manager|staff, name, email }.
interface BaseUser { id: number; tenant_id: number; role: string; name: string; email: string; }

// Adaptador núcleo → contrato del front de Let's Move.
//  - id → account_id
//  - name (un solo campo) → firstname (1ª palabra) + lastname (resto)
//  - role owner|manager → 'admin' (consola completa); staff → 'teacher' (alcance acotado)
function adaptUser(u: BaseUser): User {
  const parts = String(u.name || '').trim().split(/\s+/);
  const firstname = parts.shift() || u.email || '';
  const lastname = parts.join(' ');
  const role: User['role'] = (u.role === 'owner' || u.role === 'manager') ? 'admin'
    : (u.role === 'staff' ? 'teacher' : 'student');
  return { account_id: u.id, email: u.email, firstname, lastname, role, baseRole: u.role };
}

const AuthContext = createContext<AuthState | undefined>(undefined);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<User | null>(null);
  const [loading, setLoading] = useState(true);
  const [modules, setModules] = useState<string[] | null | undefined>(undefined);
  const [impersonator, setImpersonator] = useState<{ id: number; name: string | null } | null>(null);
  const [planReadOnly, setPlanReadOnly] = useState(false);
  const [planDueSoon, setPlanDueSoon] = useState<PlanDueSoon | null>(null);

  // Al cargar, ¿quién soy? (cookie de sesión del núcleo).
  useEffect(() => {
    api.get<{ user: BaseUser }>('/auth/me')
      .then((r) => setUser(r.user ? adaptUser(r.user) : null))
      .catch(() => setUser(null))
      .finally(() => setLoading(false));
  }, []);

  // Módulos ACTIVOS del tenant (para gatear nav/secciones). Solo operadores (admin/teacher);
  // los alumnos son contacts → /me/permissions da 403 → modules=null (su portal tiene su propio nav).
  useEffect(() => {
    if (!user) { setModules(undefined); return; }
    if (user.role === 'student') { setModules(null); return; }  // alumno: portal propio, sin gate admin
    let alive = true;
    setModules(undefined);  // operador: CARGANDO permisos → App muestra splash (evita flash de contenido no gateado)
    api.get<{ platform?: boolean; modules?: { key: string; active: boolean; level?: string }[]; impersonator?: { id: number; name: string | null } | null; planReadOnly?: boolean; planDueSoon?: PlanDueSoon | null }>('/me/permissions')
      .then((r) => {
        if (!alive) return;
        // super_admin de plataforma: bypass total en el backend → no gatear en el front tampoco.
        if (r.platform) { setModules(null); return; }
        // active = módulo del negocio encendido; level = permiso EFECTIVO del usuario
        // (rol + overrides personales). 'none' → se oculta del nav (2026-07-01).
        setModules((r.modules || []).filter((m) => m.active && m.level !== 'none').map((m) => m.key));
        setImpersonator(r.impersonator || null); // sesión "Ver como" → banner
        // Estado del plan (fase C): vencido = modo consulta; por vencer = banner con días.
        setPlanReadOnly(r.planReadOnly === true);
        setPlanDueSoon(r.planDueSoon || null);
      })
      .catch(() => { if (alive) setModules(null); });
    return () => { alive = false; };
  }, [user]);

  // null/undefined → no oculta (bypass/fallo, o durante carga el splash de App evita que se pinte).
  const hasModule = (key: string) => modules == null || modules.includes(key);

  async function login(username: string, password: string) {
    // El núcleo loguea por correo; el campo "usuario" del form se trata como email.
    const r = await api.post<{ user: BaseUser }>('/auth/login',
      { email: username.trim(), password, tenant_slug: TENANT_SLUG });
    setUser(adaptUser(r.user));
  }

  async function logout() {
    await api.post('/auth/logout').catch(() => {});
    setUser(null);
  }

  return (
    <AuthContext.Provider value={{ user, loading, modules, hasModule, impersonator, planReadOnly, planDueSoon, login, logout }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth debe usarse dentro de <AuthProvider>');
  return ctx;
}
